import React, { useState } from 'react';
import { ArrowRight, ChevronDown, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const EMobilityPage = () => {
  const evChargingData = {
    chargingStation: {
      title: 'Charging Station Storage',
      description: 'Integrated energy storage systems provide a number of options for new charging stations and existing fuel stations adding EV charging as a new revenue stream. Charging stations can offset operational costs by adopting arbitrage strategies while minimizing the impact of unplanned grid outages, demand-related grid strain, or weather events.',
      image: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80',
      features: [
        'Arbitrage for cost optimization',
        'Grid outage protection',
        'Peak demand management',
        'Revenue stream diversification',
      ],
    },
    transmission: {
      title: 'Grid Support / Transmission Asset',
      description: 'The increasing number and size of charging stations presents utility providers with the problem of slumping charge rates and unplanned outages due to spikes in grid stress. Incorporating storage as localized transmission support reduces reliance on public power grids in high-volume charging areas.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80',
      features: [
        'Reduces grid stress during peak demand',
        'Avoids costly infrastructure upgrades',
        'Maintains consistent charge rates',
        'Localized energy independence',
      ],
    },
  };

  const faqs = [
    {
      question: 'How does energy storage impact EV charging rates?',
      answer: 'Periods of high charging demand increases congestion on power grids, leading to slower charge rates. Adding energy storage allows charging stations to pull from battery capacity during high demand, avoiding slowdowns and cutting peak demand costs.',
    },
    {
      question: 'Can energy storage reduce operational costs for charging stations?',
      answer: 'Yes. EV charging stations can utilize energy storage for arbitrage - charging the system during off-peak times and using battery capacity during high demand periods to reduce peak demand charges from utility providers.',
    },
    {
      question: 'What makes Multi-Ion technology ideal for EV charging infrastructure?',
      answer: 'Multi-Ion batteries offer 15-minute fast charging, 5,000+ cycle life, and operation from -40°C to 65°C - making them perfect for demanding charging station applications where uptime and reliability are critical.',
    },
    {
      question: 'Can existing fuel stations add EV charging with energy storage?',
      answer: 'Absolutely. Our integrated energy storage systems are designed to help existing fuel stations diversify their revenue streams by adding EV charging without requiring major grid infrastructure upgrades.',
    },
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-gray-900 to-gray-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1920')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/95 to-gray-950/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">EV Charging Support</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-6">
            E-Mobility
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mb-8 leading-relaxed">
            Reliable charging infrastructure support is the key to maintaining EV adoption. Our Multi-Ion energy storage solutions help charging stations maximize uptime, reduce costs, and provide consistent charging speeds.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Contact E-Mobility Team <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown className="w-8 h-8 text-white/50 animate-bounce" />
        </div>
      </section>

      {/* EV Charging Content */}
      <div className="bg-gray-950">
        {/* Charging Station Storage */}
        <section className="py-20 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-white mb-8">{evChargingData.chargingStation.title}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <img
                  src={evChargingData.chargingStation.image}
                  alt="Charging Station Storage"
                  className="rounded-2xl w-full h-[400px] object-cover"
                />
              </div>
              <div>
                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                  {evChargingData.chargingStation.description}
                </p>
                <div className="space-y-3">
                  {evChargingData.chargingStation.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grid Support Section */}
        <section className="py-20 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-white mb-8">{evChargingData.transmission.title}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="order-2 lg:order-1">
                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                  {evChargingData.transmission.description}
                </p>
                <div className="space-y-3">
                  {evChargingData.transmission.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src={evChargingData.transmission.image}
                  alt="Grid Support"
                  className="rounded-2xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Ready to power your EV charging infrastructure?</h2>
            <p className="text-gray-400 text-lg mb-8">
              Contact our team for customized energy storage solutions for your charging stations.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
            >
              Talk to Engineering <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>

      {/* FAQs Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="text-white font-semibold pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-400 shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                  <p className="px-6 pb-6 text-gray-400">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Get In Contact</h2>
          <p className="text-gray-400 text-lg mb-8">
            Ready to discuss your E-Mobility needs? Our team is here to help.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-xl font-semibold transition-all"
          >
            Contact Sales <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EMobilityPage;
